import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { Button } from "@/components/ui/button";

const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const navLinks = [
    { href: "#services", label: "Services" },
    { href: "#why-us", label: "Why Us" },
    { href: "#testimonials", label: "Reviews" },
    { href: "/about", label: "About", isRoute: true },
    { href: "#booking", label: "Book Now" },
    { href: "#contact", label: "Contact" },
  ];

  const scrollToSection = (e, href, isRoute) => {
    if (isRoute) {
      // Let the Link component handle navigation
      return;
    }
    e.preventDefault();
    
    // Check if we're on the home page
    const isHomePage = window.location.pathname === "/";
    
    if (href.startsWith("#")) {
      if (isHomePage) {
        // On home page, just scroll to section
        const element = document.querySelector(href);
        if (element) {
          element.scrollIntoView({ behavior: "smooth" });
        }
      } else {
        // On other pages, navigate to home page with hash
        window.location.href = "/" + href;
      }
    }
    setIsMobileMenuOpen(false);
  };

  return (
    <nav
      data-testid="navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled
          ? "glass border-b border-white/20 shadow-sm"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 md:h-20">
          {/* Logo */}
          <Link
            to="/"
            onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
            className="flex items-center"
            data-testid="logo-link"
          >
            <img 
              src="https://customer-assets.emergentagent.com/job_fresh-sweep-7/artifacts/07kvlx2l_cleaners-logo.png" 
              alt="NB River City Cleaners" 
              className="h-24 md:h-36 w-auto"
            />
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-8">
            {navLinks.map((link) => (
              link.isRoute ? (
                <Link
                  key={link.href}
                  to={link.href}
                  className="nav-link text-slate-800 hover:text-[#2563EB] font-semibold text-base transition-colors"
                  data-testid={`nav-${link.label.toLowerCase().replace(" ", "-")}`}
                >
                  {link.label}
                </Link>
              ) : (
                <a
                  key={link.href}
                  href={link.href}
                  onClick={(e) => scrollToSection(e, link.href, link.isRoute)}
                  className="nav-link text-slate-800 hover:text-[#2563EB] font-semibold text-base transition-colors"
                  data-testid={`nav-${link.label.toLowerCase().replace(" ", "-")}`}
                >
                  {link.label}
                </a>
              )
            ))}
          </div>

          {/* CTA Button */}
          <div className="hidden md:block">
            <Button
              onClick={(e) => {
                e.preventDefault();
                const isHomePage = window.location.pathname === "/";
                if (isHomePage) {
                  document.querySelector("#booking")?.scrollIntoView({ behavior: "smooth" });
                } else {
                  window.location.href = "/#booking";
                }
              }}
              className="bg-[#2563EB] hover:bg-[#1D4ED8] text-white rounded-full px-6 py-2 font-semibold btn-glow transition-all"
              data-testid="nav-get-quote-btn"
            >
              Get a Quote
            </Button>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="md:hidden p-2 text-slate-700"
            data-testid="mobile-menu-btn"
          >
            {isMobileMenuOpen ? (
              <X className="w-6 h-6" />
            ) : (
              <Menu className="w-6 h-6" />
            )}
          </button>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div
            className="md:hidden glass border-t border-white/20 py-4"
            data-testid="mobile-menu"
          >
            <div className="flex flex-col gap-4">
              {navLinks.map((link) => (
                link.isRoute ? (
                  <Link
                    key={link.href}
                    to={link.href}
                    onClick={() => setIsMobileMenuOpen(false)}
                    className="text-slate-700 hover:text-[#2563EB] font-medium px-4 py-2"
                    data-testid={`mobile-nav-${link.label.toLowerCase().replace(" ", "-")}`}
                  >
                    {link.label}
                  </Link>
                ) : (
                  <a
                    key={link.href}
                    href={link.href}
                    onClick={(e) => scrollToSection(e, link.href, link.isRoute)}
                    className="text-slate-700 hover:text-[#2563EB] font-medium px-4 py-2"
                    data-testid={`mobile-nav-${link.label.toLowerCase().replace(" ", "-")}`}
                  >
                    {link.label}
                  </a>
                )
              ))}
              <div className="px-4 pt-2">
                <Button
                  onClick={(e) => {
                    e.preventDefault();
                    setIsMobileMenuOpen(false);
                    const isHomePage = window.location.pathname === "/";
                    if (isHomePage) {
                      document.querySelector("#booking")?.scrollIntoView({ behavior: "smooth" });
                    } else {
                      window.location.href = "/#booking";
                    }
                  }}
                  className="w-full bg-[#2563EB] hover:bg-[#1D4ED8] text-white rounded-full font-semibold"
                  data-testid="mobile-get-quote-btn"
                >
                  Get a Quote
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navbar;
